/**
 * How the module gets its credentials when it is mounted inside someone else's
 * page.
 *
 * Standalone, the portal owns its Keycloak session and `services/api.ts` reads
 * the token straight out of `keycloakService`. Embedded, there is no session of
 * its own — the host already authenticated the user and hands the token in. The
 * API client asks here first and falls back to Keycloak, so one client serves
 * both and neither path is a fork of the other.
 *
 * See logbook decision 12.
 */

export interface EmbedRuntime {
  /** Base URL for the reservation API. Defaults to the build-time value. */
  apiBase?: string
  /** Current access token. Called per request — never cached here. */
  getToken: () => string | undefined
  /** Value for the `X-Active-Org` header (keycloak AUTHENTICATION.md §5.1). */
  activeOrg?: string | null
  /**
   * Asked to refresh after a 401. The host owns the session, so only the host
   * can renew it. Resolve false (the default) to let the 401 surface, true to
   * retry with whatever getToken now returns. `failedToken` is the one that was
   * refused: a request still in flight when the host replaced it is not news.
   */
  onTokenExpired?: (failedToken?: string) => Promise<boolean>
  /** Base URL for live-api's streams. Defaults to the build-time value. */
  liveBase?: string
  /**
   * The station the person chose (logbook decisions 22 and 24), sent as
   * `X-Station` on every request and both live streams: the services narrow
   * the person to its zones, and what they may do there is their roles'.
   * Called per request, like getToken.
   */
  station?: () => string | undefined
}

let runtime: EmbedRuntime | null = null

export function setEmbedRuntime(next: EmbedRuntime | null): void {
  runtime = next
}

export function getEmbedRuntime(): EmbedRuntime | null {
  return runtime
}
